/**
 * Release-notes feed for /pages/updates.
 * View math is pure so node:test can lock the design's filter and paging copy.
 */

const PLATFORM_LABEL = {
  ios: "iOS app",
  android: "Android app",
  system: "System"
};

export function updatesFeedView({ releases, filter, shown, pageSize }) {
  const active = filter || "all";
  const size = pageSize > 0 ? pageSize : 6;
  const limit = shown > 0 ? shown : size;
  const matches = releases.filter((release) => active === "all" || release.platform === active);
  const visible = matches.slice(0, limit);
  const remaining = matches.length - visible.length;
  const next = Math.min(remaining, size);

  return {
    visibleIds: visible.map((release) => String(release.id)),
    countLabel: countLabel(active, matches.length),
    moreLabel: remaining > 0 ? `Show ${next} earlier updates` : "You’re at the end of the log",
    moreDisabled: remaining <= 0,
    isEmpty: matches.length === 0
  };
}

function countLabel(filter, count) {
  if (filter === "all") return `${count} updates`;
  const name = PLATFORM_LABEL[filter] || filter;
  return `${count} ${name} releases`;
}

if (typeof HTMLElement !== "undefined") {
  class UpdatesFeed extends HTMLElement {
    connectedCallback() {
      this.pageSize = Number(this.dataset.pageSize) || 6;
      this.shown = this.pageSize;
      this.filter = this.dataset.defaultFilter || "all";
      if (!this.onClick) {
        this.onClick = (event) => {
          const filterButton = event.target.closest("[data-filter]");
          if (filterButton && this.contains(filterButton)) {
            this.filter = filterButton.dataset.filter;
            this.shown = this.pageSize;
            this.render();
            return;
          }
          const more = event.target.closest("[data-more]");
          if (more && this.contains(more) && !more.disabled) {
            this.shown += this.pageSize;
            this.render();
          }
        };
        this.addEventListener("click", this.onClick);
      }
      this.render();
    }

    disconnectedCallback() {
      if (this.onClick) this.removeEventListener("click", this.onClick);
      this.onClick = null;
    }

    render() {
      const items = [...this.querySelectorAll("[data-release]")].map((el) => ({
        id: el.dataset.releaseId,
        platform: el.dataset.platform,
        el
      }));
      const view = updatesFeedView({
        releases: items,
        filter: this.filter,
        shown: this.shown,
        pageSize: this.pageSize
      });
      const visible = new Set(view.visibleIds);
      items.forEach((item) => {
        item.el.hidden = !visible.has(String(item.id));
      });
      this.querySelectorAll("[data-filter]").forEach((button) => {
        button.setAttribute("aria-pressed", button.dataset.filter === this.filter ? "true" : "false");
      });
      const count = this.querySelector("[data-count]");
      if (count) count.textContent = view.countLabel;
      const more = this.querySelector("[data-more]");
      if (more) {
        more.textContent = view.moreLabel;
        more.disabled = view.moreDisabled;
      }
      const empty = this.querySelector("[data-empty]");
      if (empty) empty.hidden = !view.isEmpty;
    }
  }

  if (!customElements.get("updates-feed")) {
    customElements.define("updates-feed", UpdatesFeed);
  }
}
